
import { Component, Prop, VModel } from 'vue-property-decorator';
import { AlertEscalationTypeEnum, AlertTimingEnum, AlertTypeEnum, CategoryAlertModel } from '@/libs/Api';
import { Guid } from '@/libs/common/functions/guid';
import Page from '@/Page.vue';
import SettingsAlertRecipientSelect from './SettingsAlertRecipientSelect.vue';

@Component({
    components: { SettingsAlertRecipientSelect },
})
export default class CategoryAlertsTable extends Page {
    @VModel({ type: Array }) alerts!: CategoryAlertModel[];
    @Prop({ required: true }) type!: AlertTypeEnum;
    @Prop({ default: () => AlertTimingEnum.Delay }) timing!: AlertTimingEnum;
    protected AlertTimingEnum: typeof AlertTimingEnum = AlertTimingEnum;

    nameRules = [(v: any) => !!v || (this.$t('validation.thisFieldIsRequired') as string)];
    daysRules = [
        (v: any) => (v !== null && v !== undefined) || (this.$t('validation.thisFieldIsRequired') as string),
        (v: any) => (v === null || v === undefined || isNaN(Number(v)) ? (this.$t('common.enterAnInteger') as string) : true),
        (v: any) => (Number(v) >= 1 ? true : (this.$t('common.mustBeMoreOrEqual', { value: 1 }) as string)),
    ];
    recipientRules = [
        (v: any) => (Array.isArray(v) && v.length > 0) || (this.$t('validation.thisFieldIsRequired') as string),
    ];

    // Dialog state
    dialogVisible = false;
    dialogValid = true;
    dialogSaving = false;
    dialogModel: CategoryAlertModel | null = null;
    dialogOnDay = true;
    dialogIsRange = false;

    get daysToRules() {
        return [
            (v: any) => (this.dialogIsRange ? v !== null && v !== undefined : true) || (this.$t('validation.thisFieldIsRequired') as string),
            (v: any) => (!this.dialogIsRange || v === null || v === undefined || !isNaN(Number(v)) ? true : (this.$t('common.enterAnInteger') as string)),
            (v: any) => (!this.dialogIsRange || Number(v) >= 1 ? true : (this.$t('common.mustBeMoreOrEqual', { value: 1 }) as string)),
            (v: any) => {
                if (!this.dialogIsRange) return true;
                const from = Number(this.dialogModel?.daysDelay ?? 0);
                const to = Number(v);
                return to >= from ? true : (this.$t('settings.categories.daysRangeInvalid') as string);
            },
        ];
    }


    get alertsByType(): CategoryAlertModel[] {
        const list = this.alerts ?? [];
        const linq = list as any;
        if (linq && typeof linq.where === 'function') {
            return linq
                .where((x: CategoryAlertModel) => x.type == this.type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing)
                .toArray();
        }
        return list.filter((x) => x.type == this.type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing);
    }

    get sectionTitleKey(): string {
        if (this.timing === AlertTimingEnum.Delay) {
            switch (this.type) {
                case AlertTypeEnum.Performance:
                    return 'settings.categories.alertsPerformance';
                case AlertTypeEnum.Finding:
                    return 'settings.categories.alertsFinding';
                case AlertTypeEnum.MeasureImplementation:
                    return 'settings.categories.alertsMeasureImplementation';
                default:
                    return 'settings.categories.notificationAlerts';
            }
        } else {
            switch (this.type) {
                case AlertTypeEnum.Performance:
                    return 'settings.categories.upcomingPerformance';
                case AlertTypeEnum.Finding:
                    return 'settings.categories.upcomingFinding';
                case AlertTypeEnum.MeasureImplementation:
                    return 'settings.categories.upcomingMeasureImplementation';
                default:
                    return 'settings.categories.upcomingAlerts';
            }
        }
    }

    openAddDialog() {
        const list = this.alerts ?? [];
        const nextIndex = list.filter((x) => x.type == this.type && (x.alertTiming ?? AlertTimingEnum.Delay) == this.timing)
            .length + 1;
        this.dialogModel = {
            id: Guid.EmptyGuid(),
            name: this.$t('settings.categories.level', { index: nextIndex }) as string,
            daysDelay: this.timing === AlertTimingEnum.Delay ? 1 : 0,
            type: this.type,
            alertTiming: this.timing,
            alertEscalations: this.timing === AlertTimingEnum.Before ? [{ type: AlertEscalationTypeEnum.Owner } as any] : [],
        } as CategoryAlertModel;
        this.dialogOnDay = this.timing === AlertTimingEnum.Before;
        this.dialogIsRange = false;
        if (this.dialogModel) {
            this.dialogModel.daysDelayTo = null as any;
            (this.dialogModel as any).everyDay = false;
        }
        this.dialogVisible = true;
    }

    openEditDialog(alert: CategoryAlertModel) {
        this.dialogModel = JSON.parse(JSON.stringify(alert));
        const model = this.dialogModel as CategoryAlertModel;
        this.dialogOnDay = (model.alertTiming ?? AlertTimingEnum.Delay) === AlertTimingEnum.Before && (model.daysDelay ?? 0) === 0;
        if ((model.alertTiming ?? AlertTimingEnum.Delay) === AlertTimingEnum.Before) {
            model.alertEscalations = [{ type: AlertEscalationTypeEnum.Owner } as any];
        }
        // Normalize everyDay to boolean
        (model as any).everyDay = (model as any).everyDay === true;
        this.dialogIsRange = (model.daysDelayTo ?? null) != null;
        this.dialogVisible = true;
    }

    closeDialog() {
        this.dialogVisible = false;
        this.dialogModel = null;
    }

    get dialogHeader(): string {
        return this.dialogModel && this.dialogModel.id == Guid.EmptyGuid()
            ? (this.$t('settings.categories.addLevel') as string)
            : this.dialogModel?.name ?? '';
    }

    saveDialog() {
        if (!this.dialogModel) return;
        const form: any = this.$refs.form;
        if (form && typeof form.validate === 'function') {
            const ok = form.validate();
            const hasRecipients = this.timing === AlertTimingEnum.Delay ? (this.dialogModel.alertEscalations ?? []).length > 0 : true;
            if (!ok || !hasRecipients) {
                return;
            }
        }
        if (this.timing === AlertTimingEnum.Before) {
            this.dialogModel.alertEscalations = [{ type: AlertEscalationTypeEnum.Owner } as any];
            if (this.dialogOnDay === true) {
                (this.dialogModel as any).daysDelay = 0;
                (this.dialogModel as any).daysDelayTo = null;
                (this.dialogModel as any).everyDay = false;
            }
        }
        if (!this.dialogIsRange && this.dialogModel) {
            (this.dialogModel as any).daysDelayTo = null;
            (this.dialogModel as any).everyDay = false;
        }
        if (this.dialogIsRange && this.dialogModel) {
            const from = Number(this.dialogModel.daysDelay ?? 0);
            const to = Number(this.dialogModel.daysDelayTo ?? 0);
            if (to < from) {
                (this.dialogModel as any).daysDelayTo = from;
            }
        }

        const list = (this.alerts ?? []) as CategoryAlertModel[];
        const isCreate = !this.dialogModel.id || this.dialogModel.id == Guid.EmptyGuid();
        if (isCreate) {
            list.push(this.dialogModel as CategoryAlertModel);
        } else {
            const idx = list.findIndex((x) => x.id === this.dialogModel!.id);
            if (idx >= 0) {
                list.splice(idx, 1, this.dialogModel as CategoryAlertModel);
            }
        }
        this.$emit('input', list);
        this.closeDialog();
    }

    // Delete confirm
    deleteDialog = false;
    selectedToDelete: CategoryAlertModel | null = null;
    deletingSelected = false;

    confirmDelete(alert: CategoryAlertModel) {
        this.selectedToDelete = alert;
        this.deleteDialog = true;
    }

    doDeleteSelected() {
        if (!this.selectedToDelete) {
            this.deleteDialog = false;
            return;
        }
        const list = this.alerts ?? [];
        const idx = list.indexOf(this.selectedToDelete);
        if (idx >= 0) {
            list.splice(idx, 1);
            this.$emit('input', list);
        }
        this.selectedToDelete = null;
        this.deleteDialog = false;
    }

    hasRecipients(alert: CategoryAlertModel): boolean {
        return (alert.alertEscalations ?? []).length > 0;
    }

    mounted() {
        this.normalizeUpcomingRecipients();
    }

    private normalizeUpcomingRecipients() {
        const list = this.alerts ?? [];
        list.forEach((a) => {
            const timing = (a.alertTiming ?? AlertTimingEnum.Delay);
            if (timing === AlertTimingEnum.Before) {
                const hasOnlyOwner = (a.alertEscalations ?? []).length === 1 && (a.alertEscalations ?? [])[0].type === AlertEscalationTypeEnum.Owner;
                if (!hasOnlyOwner) {
                    a.alertEscalations = [{ type: AlertEscalationTypeEnum.Owner } as any];
                }
            }
        });
    }

    isOnDay(alert: CategoryAlertModel): boolean {
        return (alert.daysDelay ?? 0) === 0;
    }

    getAlertRecipientDisplay(alert: CategoryAlertModel): string {
        const parts: string[] = [];
        const escalations = (alert.alertEscalations ?? []) as any[];
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.Owner)) {
            parts.push(this.$t('settings.categories.escalationType.owner') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.DirectSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.directSupervisor') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.SecondLevelSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.secondLevelSupervisor') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.ThirdLevelSupervisor)) {
            parts.push(this.$t('settings.categories.escalationType.thirdLevelSupervisor') as string);
        }
        if (escalations.some((x) => x.type === AlertEscalationTypeEnum.TenantUser)) {
            parts.push(this.$t('settings.categories.escalationType.tenantUser') as string);
        }
        return parts.join(', ');
    }
}
